import {useMemo, useState, useSyncExternalStore, type CSSProperties} from "react";
import cn from "classnames";
import {useQueries, useQuery, useQueryClient} from "@tanstack/react-query";

import {API} from "api";
import {useSetting} from "contexts/setting";
import {useImages} from "contexts/images";
import {PRODUCTS} from "data/products.js";
import {useChannelSettings} from "hooks/useChannelSettings";
import {useCurrentSet} from "hooks/useCurrentSet";
import {useBgDerivation} from "hooks/useBgDerivation";
import {useTemplateSelection} from "hooks/useTemplateSelection";
import {isProductDefaultAuthoritative} from "libs/authoritativeDefaultScene";
import {BAKED_BG_CHANNELS} from "libs/bakedBg";
import {loadBgFingerprint, type BgFingerprint} from "libs/bgFingerprint";
import {getLogoCycle, getLogoUrl} from "libs/logo";
import {bgOpsSnapshot, getBgOps, subscribeBgOps} from "libs/bgOps";
import type {CatalogBackgroundLike} from "libs/bgCatalogIndex";
import {getRouteTemplates} from "utils/routeTemplates";
import {lastAdjustedCandidate, previewCandidates} from "../BgSelector/tilePreviewGeometry";
import BatchBgUpload from "./BatchBgUpload";
import {shadowedCropTarget, type OverrideRow} from "./cropRowTarget";
import {
  comparisonGroups,
  rankMatches,
  replicaGallery,
  replicaProductCodes,
  type ReplicaColours,
  type ReplicaLook,
  type ReplicaMatch,
  type ReplicaSource,
  type ReplicaTile,
} from "./replicateBg";
import {
  runReplica,
  undoReplica,
  type ReplicaProductResult,
  type ReplicaRowAddress,
  type ReplicaTarget,
  type ReplicaUndoEntry,
} from "./replicaRun";

/**
 * "Copy to other products" in the Defaults panel (replicateBg.ts): the
 * position of the background on screen goes to the same picture of the other
 * products of the channel, on this stage and set.
 *
 * The section loads nothing until it is opened. Then it finds the picture in
 * each gallery by its composition and checks the products where it is
 * certainly the same picture. The admin can choose another tile for a
 * product, or clear it. The copy takes what the banners of this page show,
 * as Save does; Save itself still saves this product.
 */

interface Option {
  label: string;
  value: string;
}

interface PositionSource {
  src: string;
  width: number;
  height: number;
  transform: ReplicaSource["transform"];
  sharedImage: boolean;
}

interface Props {
  /** Every row of the channel (the panel's list). */
  overrides: OverrideRow[] | null | undefined;
  /** The page shows its default, and the writes can be made. */
  canWrite: boolean;
  /** The tile number of the background on screen, for the labels. */
  backgroundNumber: string;
}

/** Load the fingerprints of at most this many thumbnails at the same time. */
const LOAD_CONCURRENCY = 6;

async function mapLimited<T, R>(items: readonly T[], limit: number, fn: (item: T) => Promise<R>) {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      out[index] = await fn(items[index]!);
    }
  };
  await Promise.all(Array.from({length: Math.min(limit, items.length)}, worker));
  return out;
}

/**
 * The tiles of a gallery, best match first. The likely tiles are compared
 * first (comparisonGroups); the others load only when none of them is the same
 * picture, or when `all` is set.
 */
async function matchGallery(
  source: {
    variation: string;
    catalogAspect: number | null;
    catalogSize: string | null;
    fingerprint: BgFingerprint;
  },
  gallery: readonly ReplicaTile[],
  all: boolean
): Promise<{matches: ReplicaMatch[]; complete: boolean}> {
  const fingerprints = async (tiles: readonly ReplicaTile[]) =>
    mapLimited(tiles, LOAD_CONCURRENCY, async (tile) => ({
      tile,
      fingerprint: await loadBgFingerprint(tile.thumbnailUrl).catch((error) => {
        console.warn(`[ReplicateBgSection] No fingerprint for ${tile.thumbnailUrl}:`, error);
        return null;
      }),
    }));
  if (!all) {
    for (const group of comparisonGroups(source, gallery)) {
      const matches = rankMatches(source, await fingerprints(group));
      if (matches[0]?.level === "same") {
        return {matches, complete: group.length === gallery.length};
      }
    }
  }
  return {matches: rankMatches(source, await fingerprints(gallery)), complete: true};
}

const LEVEL_TEXT: Record<ReplicaMatch["level"], string> = {
  same: "same picture",
  similar: "similar, check it",
  different: "no matching picture",
};

const percent = (similarity: number | null) =>
  similarity === null ? "—" : `${Math.round(Math.max(0, similarity) * 100)}%`;

export default function ReplicateBgSection({overrides, canWrite, backgroundNumber}: Props) {
  const {
    dataChannel: channel,
    dataType: type,
    product,
    layoutOption,
    bgOption,
    visible,
    hiddenTemplates,
    extraTemplates,
    base,
    bases,
  } = useSetting() as unknown as {
    dataChannel: string;
    dataType: string;
    product: Option;
    layoutOption: number | string;
    bgOption: string | number;
    visible: string[];
    hiddenTemplates: string[];
    extraTemplates: string[];
    base: string | null | undefined;
    bases: Record<string, unknown> | null | undefined;
  };
  const {
    bgAdjustmentOrder,
    bgOpacities,
    bgBrightnesses,
    bgBrightness,
    bgOpacity,
    bgBlurValue,
    textColorLocked,
    logoVariants,
  } = useImages() as unknown as {
    bgAdjustmentOrder: Record<string, string[]>;
    bgOpacities: Record<string, number | undefined> | null;
    bgBrightnesses: Record<string, number | undefined> | null;
    bgBrightness: number | undefined;
    bgOpacity: number | undefined;
    bgBlurValue: number | undefined;
    textColorLocked: Record<string, unknown> | null | undefined;
    logoVariants: Record<string, unknown> | null | undefined;
  };
  const set: string | null = useCurrentSet() || null;
  const selection = useTemplateSelection();
  const bgDerivation = useBgDerivation();
  const queryClient = useQueryClient();
  const {data: settings} = useChannelSettings(channel);

  const layout = String(+layoutOption || 1);
  const background = String(bgOption);
  const [isOpen, setIsOpen] = useState(false);
  const [copyLook, setCopyLook] = useState(true);
  const [copyColours, setCopyColours] = useState(true);
  // The admin's own choices; a product without one follows its best match.
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [progress, setProgress] = useState<{phase: string; done: number; total: number} | null>(
    null
  );
  const [runResults, setRunResults] = useState<ReplicaProductResult[] | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [undo, setUndo] = useState<ReplicaUndoEntry[] | null>(null);
  const [undoState, setUndoState] = useState<"running" | "done" | "failed" | null>(null);

  // The choices belong to one background of one page.
  const pageKey = [channel, type, set, product.value, layout, background].join("|");
  const [choicesKey, setChoicesKey] = useState(pageKey);
  if (choicesKey !== pageKey) {
    setChoicesKey(pageKey);
    setPicks({});
    setChecks({});
    setExpanded(null);
    setRunResults(null);
    setRunError(null);
    setUndo(null);
    setUndoState(null);
  }

  const labelOf = (code: string) => PRODUCTS.find((p: Option) => p.value === code)?.label ?? code;
  const codes = useMemo(
    () =>
      settings === undefined
        ? []
        : replicaProductCodes({channel, type, set, product: product.value, settings}).sort((a, b) =>
            labelOf(a).localeCompare(labelOf(b))
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channel, type, set, product.value, settings]
  );

  // The banners of the page that show the gallery picture with a position.
  const registered = useSyncExternalStore(subscribeBgOps, bgOpsSnapshot);
  const routeTemplates = useMemo(
    () =>
      getRouteTemplates(channel, {
        layoutOption,
        set,
        product: product.value,
        type,
        hiddenTemplates,
        extraTemplates,
        selection,
      }),
    [channel, layoutOption, set, product.value, type, hiddenTemplates, extraTemplates, selection]
  );
  const pageBannerIds = useMemo(() => {
    const shown = new Set(visible);
    const positioned = new Set(registered);
    return routeTemplates.map((t) => t.id).filter((id) => shown.has(id) && positioned.has(id));
  }, [routeTemplates, visible, registered]);

  const galleryOf = (code: string, backgrounds: CatalogBackgroundLike[]) =>
    replicaGallery({backgrounds, product: code, type, set, settings: settings ?? null});
  const fetchCatalog = async (code: string) => {
    const response = await API.fetchBackgroundsCatalog(channel, type, code);
    if (!Array.isArray(response?.backgrounds)) {
      throw new Error(`Invalid backgrounds response for ${code} — backgrounds missing`);
    }
    return response.backgrounds as CatalogBackgroundLike[];
  };

  const sourceCatalog = useQuery({
    queryKey: ["replica-catalog", channel, type, product.value],
    queryFn: () => fetchCatalog(product.value),
    enabled: isOpen,
    staleTime: 60 * 1000,
  });
  const sourceTile = useMemo(
    () =>
      sourceCatalog.data
        ? (galleryOf(product.value, sourceCatalog.data).find((t) => t.variation === background) ??
          null)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceCatalog.data, product.value, background, settings, type, set]
  );
  const sourceFingerprint = useQuery({
    queryKey: ["bg-fingerprint", sourceTile?.thumbnailUrl],
    queryFn: () => loadBgFingerprint(sourceTile!.thumbnailUrl),
    enabled: isOpen && Boolean(sourceTile),
    staleTime: Infinity,
  });

  const catalogs = useQueries({
    queries: codes.map((code) => ({
      queryKey: ["replica-catalog", channel, type, code],
      queryFn: () => fetchCatalog(code),
      enabled: isOpen,
      staleTime: 60 * 1000,
    })),
  });
  const galleries = useMemo(
    () =>
      codes.map((code, index) => {
        const data = catalogs[index]?.data;
        return data ? galleryOf(code, data) : null;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [codes, catalogs.map((query) => query.dataUpdatedAt).join(","), settings, type, set]
  );
  const source =
    sourceTile && sourceFingerprint.data
      ? {
          variation: sourceTile.variation,
          catalogAspect: sourceTile.catalogAspect,
          catalogSize: sourceTile.catalogSize,
          fingerprint: sourceFingerprint.data,
        }
      : null;
  const matchQuery = (code: string, index: number, all: boolean, enabled: boolean) => {
    const gallery = galleries[index];
    return {
      queryKey: [
        "replica-match",
        sourceTile?.thumbnailUrl,
        background,
        code,
        all,
        gallery?.map((tile) => `${tile.variation}=${tile.thumbnailUrl}`).join(","),
      ],
      queryFn: () => matchGallery(source!, gallery!, all),
      enabled: enabled && isOpen && Boolean(source) && Boolean(gallery),
      staleTime: Infinity,
    };
  };
  // The tile with the identity of the source first; every tile when the admin
  // opens the strip of a product and the first query did not compare them.
  const firstMatches = useQueries({
    queries: codes.map((code, index) => matchQuery(code, index, false, true)),
  });
  const allMatches = useQueries({
    queries: codes.map((code, index) =>
      matchQuery(code, index, true, expanded === code && firstMatches[index]?.data?.complete === false)
    ),
  });

  interface Row {
    code: string;
    gallery: ReplicaTile[] | null;
    matches: ReplicaMatch[] | null;
    complete: boolean;
    chosen: ReplicaMatch | null;
    checked: boolean;
    isLoading: boolean;
    error: string | null;
  }
  const rows: Row[] = codes.map((code, index) => {
    const first = firstMatches[index];
    const data = allMatches[index]?.data ?? first?.data;
    const matches = data?.matches ?? null;
    const pick = picks[code];
    const chosen =
      (pick !== undefined ? matches?.find((m) => m.tile.variation === pick) : matches?.[0]) ?? null;
    // Checked by default: the same picture, or a tile the admin chose.
    const checked = Boolean(chosen) && (checks[code] ?? (pick !== undefined || chosen?.level === "same"));
    const error = catalogs[index]?.error ?? first?.error;
    return {
      code,
      gallery: galleries[index] ?? null,
      matches,
      complete: data?.complete ?? false,
      chosen,
      checked,
      isLoading: Boolean(catalogs[index]?.isLoading || first?.isLoading),
      error: error ? (error instanceof Error ? error.message : String(error)) : null,
    };
  });
  const checkedRows = rows.filter((row) => row.checked && row.chosen);
  const matchedCount = rows.filter((row) => row.chosen?.level === "same").length;
  const isRunning = progress !== null;

  const writeRow = async (
    address: ReplicaRowAddress,
    sceneData: Record<string, unknown>,
    previous: Record<string, unknown> | null
  ) => {
    const row = rowOf(address);
    if (bgDerivation) await API.patchDefaultScene({...row, sceneData});
    else await API.setDefaultScene({...row, sceneData: {...(previous ?? {}), ...sceneData}});
  };
  const rowOf = (address: ReplicaRowAddress) => ({
    channel,
    product: address.product,
    type,
    set: set ?? undefined,
    layout: address.layout || undefined,
    background: address.background || undefined,
    variant: address.variant || undefined,
  });

  const refreshQueries = (products: string[]) => {
    queryClient.invalidateQueries({queryKey: ["default-scene-overrides"]});
    queryClient.invalidateQueries({queryKey: ["v3-resolved-default-all"]});
    for (const code of products) {
      queryClient.invalidateQueries({queryKey: ["v3-resolved-default", channel, code]});
      queryClient.invalidateQueries({queryKey: ["v3-resolved-default", "batch", channel, code]});
    }
  };

  /** What each banner of the page gives the copy, read now. */
  const readSources = () => {
    const sources: Record<string, ReplicaSource> = {};
    let sourceImageUrl: string | null = null;
    for (const id of pageBannerIds) {
      const position = (
        getBgOps(id) as {positionSource?: () => PositionSource | null} | null
      )?.positionSource?.();
      if (!position || !position.sharedImage) continue;
      if (!(position.width > 0) || !(position.height > 0)) continue;
      if (sourceImageUrl !== null && position.src !== sourceImageUrl) {
        throw new Error(`The banners show two gallery images: ${sourceImageUrl}, ${position.src}`);
      }
      sourceImageUrl = position.src;
      sources[id] = {
        transform: {
          x: position.transform.x,
          y: position.transform.y,
          scale: position.transform.scale,
          rotate: position.transform.rotate ?? 0,
          flip: {x: Boolean(position.transform.flip?.x), y: Boolean(position.transform.flip?.y)},
        },
        width: position.width,
        height: position.height,
        opacity: bgOpacities?.[id],
        brightness: bgBrightnesses?.[id],
      };
    }
    if (!sourceImageUrl) throw new Error("No banner of this page shows the gallery picture");
    return {sources, sourceImageUrl};
  };

  const handleCopy = async () => {
    setRunError(null);
    setRunResults(null);
    setUndoState(null);
    try {
      if (!overrides) throw new Error("The rows of the channel have not loaded");
      const {sources, sourceImageUrl} = readSources();
      const cropRow = shadowedCropTarget({
        overrides,
        channel,
        product: product.value,
        type,
        set,
        layout,
        background,
        variant: null,
        chosenProduct: product.value,
        chosenLayout: null,
        chosenBackground: background,
        chosenVariant: null,
      });
      const look: ReplicaLook | null = copyLook ? {bgBrightness, bgOpacity, bgBlurValue} : null;
      const colours: ReplicaColours | null = copyColours
        ? {base, bases, textColorLocked, logoVariants}
        : null;
      const preview = lastAdjustedCandidate(
        previewCandidates(routeTemplates, visible),
        bgAdjustmentOrder[background]
      );
      const targets: ReplicaTarget[] = checkedRows.map((row) => ({
        product: row.code,
        background: row.chosen!.tile.variation,
        imageUrl: row.chosen!.tile.imageUrl,
      }));
      setProgress({phase: "writing", done: 0, total: targets.length});
      const {results, undo: undoEntries} = await runReplica({
        channel,
        type,
        set,
        layout,
        overrides,
        sourceLayout: cropRow?.background === background ? cropRow.layout : null,
        sources,
        sourceImageUrl,
        look,
        colours,
        sourceProduct: product.value,
        logoChoices: (code) => ({
          cycle: getLogoCycle(code, type, layoutOption, channel),
          preferred: getLogoUrl(code, [], type, channel),
        }),
        previewTemplate: preview?.id ?? null,
        targets,
        targetTemplateIds: (code, scene) =>
          getRouteTemplates(channel, {
            layoutOption,
            set,
            product: code,
            type,
            hiddenTemplates: (scene?.hiddenTemplates as string[] | undefined) ?? [],
            extraTemplates: (scene?.extraTemplates as string[] | undefined) ?? [],
            selection,
          }).map((t) => t.id),
        bake: BAKED_BG_CHANNELS.has(channel),
        api: {
          imageSizes: async (urls) => {
            const {sizes} = await API.fetchBgImageSizes(urls);
            if (!sizes || typeof sizes !== "object") {
              throw new Error("Invalid image-sizes response — sizes missing");
            }
            return sizes;
          },
          resolvedScene: async (target) => {
            const resolved = await API.fetchResolvedDefaultScene({
              channel,
              product: target.product,
              type,
              set: set ?? undefined,
              layout,
              background: target.background,
            });
            if (!resolved || !("scene_data" in resolved)) {
              throw new Error(`Invalid resolved default for ${target.product} — scene_data missing`);
            }
            return {
              scene: resolved.scene_data ?? null,
              productDefaultShown: isProductDefaultAuthoritative(resolved, target.product),
            };
          },
          writeRow,
          bakeBackground: (bakeSource) => API.bakeBackground(bakeSource),
        },
        onProgress: setProgress,
      });
      setRunResults(results);
      setUndo(undoEntries.length > 0 ? undoEntries : null);
      refreshQueries(targets.map((target) => target.product));
    } catch (error) {
      console.error("[ReplicateBgSection] Copy failed:", error);
      setRunError(error instanceof Error ? error.message : String(error));
    } finally {
      setProgress(null);
    }
  };

  const handleUndo = async () => {
    if (!undo) return;
    setUndoState("running");
    try {
      await undoReplica(undo, {
        restoreRow: (address, sceneData) => API.setDefaultScene({...rowOf(address), sceneData}),
        deleteRow: (address) => API.deleteDefaultScene(rowOf(address)),
      });
      setUndo(null);
      setRunResults(null);
      setUndoState("done");
      refreshQueries([...new Set(undo.map((entry) => entry.address.product))]);
    } catch (error) {
      console.error("[ReplicateBgSection] Undo failed:", error);
      setUndoState("failed");
    }
  };

  const linkOf = (code: string, variation: string) => {
    const params = new URLSearchParams();
    if (set) params.set("set", set);
    params.set("layout", layout);
    params.set("background", variation);
    return `/${channel}/${code}/${type}?${params}`;
  };

  const choose = (code: string, variation: string) => {
    setPicks((prev) => ({...prev, [code]: variation}));
    setChecks((prev) => ({...prev, [code]: true}));
    setExpanded(null);
  };

  return (
    <div className="default-scene-control selector-visibility-section replicate-bg-section">
      <span
        className="default-scene-caption"
        title="The same picture, coloured for each product: copy the position of this background to them"
      >
        Copy to other products
      </span>
      <details
        className="selector-visibility-group"
        onToggle={(event) => setIsOpen((event.target as HTMLDetailsElement).open)}
      >
        <summary className="selector-visibility-summary">
          <span>{`Position of bg ${backgroundNumber} → other products`}</span>
          <span className="selector-visibility-count">
            {isOpen ? `${matchedCount} of ${codes.length} matched` : `${codes.length} products`}
          </span>
        </summary>
        {isOpen && (
          <div className="replicate-bg-body">
            <BatchBgUpload
              channel={channel}
              type={type}
              set={channel === "releases" ? set : null}
              products={[product.value, ...codes].map((code) => ({value: code, label: labelOf(code)}))}
              product={product.value}
              disabled={!canWrite || isRunning}
            />
            <span className="replicate-bg-note">
              {`Copies position, zoom, turn and flip of ${pageBannerIds.length} banner${pageBannerIds.length === 1 ? "" : "s"} as this page shows them, to the same picture of each checked product. Save saves this product.`}
            </span>
            <label className="default-scene-field">
              <input
                type="checkbox"
                checked={copyLook}
                onChange={(event) => setCopyLook(event.target.checked)}
              />
              Brightness, opacity and blur too
            </label>
            <label
              className="default-scene-field"
              title="The base colour, and per banner its base, text colour and logo. A logo goes as the same style of the product's own logo."
            >
              <input
                type="checkbox"
                checked={copyColours}
                onChange={(event) => setCopyColours(event.target.checked)}
              />
              Base, text and logo colours too
            </label>

            {sourceCatalog.isSuccess && !sourceTile && (
              <span className="default-scene-status warning">
                This background is not a tile of the gallery: nothing to match.
              </span>
            )}
            {sourceFingerprint.isError && (
              <span className="default-scene-status warning">
                Could not read the picture of this background.
              </span>
            )}

            <div className="replicate-bg-rows">
              {rows.map((row) => (
                <div key={row.code} className="replicate-bg-product">
                  <div className={cn("replicate-bg-row", {"is-off": !row.checked})}>
                    <input
                      type="checkbox"
                      aria-label={`Copy to ${labelOf(row.code)}`}
                      checked={row.checked}
                      disabled={!row.chosen || isRunning}
                      onChange={(event) =>
                        setChecks((prev) => ({...prev, [row.code]: event.target.checked}))
                      }
                    />
                    <span className="replicate-bg-product-label" title={row.code}>
                      {labelOf(row.code)}
                    </span>
                    {row.chosen ? (
                      <span
                        className={cn("replicate-bg-match", `is-${row.chosen.level}`)}
                        data-background={row.chosen.tile.variation}
                        title={`${labelOf(row.code)} bg ${row.chosen.tile.number}: ${LEVEL_TEXT[row.chosen.level]}${row.chosen.otherAspect ? ", other proportions" : ""}`}
                      >
                        <span
                          className="replicate-bg-thumb"
                          style={{"--thumb": `url("${row.chosen.tile.thumbnailUrl}")`} as CSSProperties}
                        />
                        {`Bg ${row.chosen.tile.number} · ${percent(row.chosen.similarity)}`}
                      </span>
                    ) : (
                      <span className="replicate-bg-match">
                        {row.error ?? (row.isLoading ? "Finding the picture…" : "No tiles")}
                      </span>
                    )}
                    {row.gallery && row.gallery.length > 0 && (
                      <button
                        type="button"
                        className="replicate-bg-change"
                        onClick={() => setExpanded(expanded === row.code ? null : row.code)}
                        disabled={isRunning}
                      >
                        {expanded === row.code ? "Close" : "Change"}
                      </button>
                    )}
                  </div>
                  {expanded === row.code && (
                    <div className="replicate-bg-strip">
                      {!row.complete && <span className="replicate-bg-note">Comparing every tile…</span>}
                      {(row.complete ? row.matches : null)
                        ?.slice()
                        .sort((a, b) => a.tile.number - b.tile.number)
                        .map((match) => (
                          <button
                            key={match.tile.variation}
                            type="button"
                            className={cn("selector-visibility-bg replicate-bg-tile", {
                              "is-current": row.chosen?.tile.variation === match.tile.variation,
                            })}
                            onClick={() => choose(row.code, match.tile.variation)}
                            title={`${labelOf(row.code)} bg ${match.tile.number}: ${LEVEL_TEXT[match.level]}`}
                          >
                            <span
                              className="selector-visibility-bg-thumb"
                              style={{"--thumb": `url("${match.tile.thumbnailUrl}")`} as CSSProperties}
                            />
                            <span className="selector-visibility-bg-number">
                              {`${match.tile.number} · ${percent(match.similarity)}`}
                            </span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="default-scene-row">
              {undo && (
                <button
                  type="button"
                  className="default-scene-btn default-scene-undo"
                  onClick={handleUndo}
                  disabled={isRunning || undoState === "running"}
                  title="Put the rows of the copy back as they were"
                >
                  {undoState === "running" ? "..." : "Undo"}
                </button>
              )}
              <button
                type="button"
                className="default-scene-btn replicate-bg-copy"
                onClick={handleCopy}
                disabled={!canWrite || isRunning || checkedRows.length === 0 || pageBannerIds.length === 0}
                title={
                  canWrite
                    ? `Copy the position of bg ${backgroundNumber} to ${checkedRows.map((row) => labelOf(row.code)).join(", ")}`
                    : "The page is still loading its default"
                }
              >
                {isRunning
                  ? "..."
                  : `Copy to ${checkedRows.length} product${checkedRows.length === 1 ? "" : "s"}`}
              </button>
            </div>

            {progress && (
              <span className="default-scene-status">
                {progress.phase === "writing"
                  ? `Writing ${progress.done} of ${progress.total} products…`
                  : `Cutting the backgrounds (${progress.done} of ${progress.total}). Keep this page open.`}
              </span>
            )}
            {runError && <span className="default-scene-status warning">{`Not copied: ${runError}`}</span>}
            {undoState === "done" && (
              <span className="default-scene-status success">Restored the rows of the copy</span>
            )}
            {undoState === "failed" && <span className="default-scene-status warning">Undo failed</span>}
            {runResults && (
              <ul className="replicate-bg-results">
                {runResults.map((result) => (
                  <li
                    key={result.product}
                    className={cn({"is-failed": result.error !== null})}
                    data-product={result.product}
                  >
                    <a href={linkOf(result.product, result.background)} target="_blank" rel="noreferrer">
                      {labelOf(result.product)}
                    </a>
                    {result.error !== null
                      ? ` — not copied: ${result.error}`
                      : ` — ${result.templates.length} banner${result.templates.length === 1 ? "" : "s"}${
                          result.skipped.some((s) => s.reason === "own-image")
                            ? `; kept their own image: ${result.skipped
                                .filter((s) => s.reason === "own-image")
                                .map((s) => s.id)
                                .join(", ")}`
                            : ""
                        }${result.unbaked.length > 0 ? `; not cut: ${result.unbaked.join(", ")}` : ""}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </details>
    </div>
  );
}
